import React from 'react';
import { Box, HStack, Skeleton, Spacer, VStack } from 'native-base';
import {
    faAngleDown,
    faAngleUp,
    faGift,
    faTrophy,
} from '@fortawesome/free-solid-svg-icons';
import { i18nFn, MemberMilestones } from '@springbig/shared';
import Image from '~/shared/Image';
import SliderBar from '~/shared/RangeBar';
import { Caption, H3 } from '~/shared/Typography';
import { SpIcon } from '~/shared/Icon';
import { useGeneralStyles, useMilestonesStyles } from '~/shared/styles';
import Pressable from '~/shared/Pressable';

type CardMilestoneProps = {
    handleMilestone: () => void;
    currentValue?: number;
    goal: number;
    milestone: MemberMilestones;
    t: i18nFn;
    type: 'spend' | 'points' | 'visits';
};

const CardMilestone = ({
    currentValue,
    goal,
    milestone,
    t,
    type,
}: CardMilestoneProps) => {

    const [openMilestone, setOpenMilestone] = React.useState<boolean>(false);
    const {
        text,
        icon
    } = useMilestonesStyles();
    const { section_background } = useGeneralStyles();

    const getRewardNames = (milestone: MemberMilestones): string => {

        return milestone.rewards.map(reward => reward.item_name?.replace(/\| /g, '\n')).join(', ')
    };

    return (

        // <H3 text='auth.location.what_state' mb={3} textValues={[`${merchantCountry}`, merchantCountry === 'CA' ? 'province' : 'state']} alignSelf={'center'} />

        <Box bg={section_background} p={4} marginY={2}>
            <Pressable
                onPress={() => {
                    if (milestone.rewards?.length) setOpenMilestone(!openMilestone)
                }}
            >
                <Box flexDirection={'row'} justifyContent={'space-between'} w={'80%'}>
                    <Box flexDirection={'row'} mb={2} alignItems={'center'}>
                        <Caption color={text}>
                            { milestone.name }{' '}
                        </Caption>
                        { currentValue >= goal &&
                            <SpIcon size={14} color={icon} icon={faTrophy} />
                        }
                    </Box>
                    <Caption
                        color={text}
                        fontSize={12}
                        opacity={.5}
                    >
                        { currentValue <= goal ? currentValue : goal }/{ goal }
                    </Caption>
                </Box>
                <Box flexDirection={'row'} w={'100%'}>
                    <SliderBar defaultValue={currentValue} maxValue={goal} w={'85%'} />
                    { milestone.rewards.length > 0 &&
                        <Pressable
                            w={'15%'}
                            flexDirection={'row'}
                            justifyContent='flex-end'
                            onPress={() => setOpenMilestone(!openMilestone)}
                        >
                            { openMilestone ?
                                <SpIcon color={icon} icon={faAngleUp} />
                            :
                                <SpIcon color={icon} icon={faAngleDown} />
                            }

                        </Pressable>
                    }
                </Box>
                { milestone.achieved ?
                    <Caption
                        fontSize={12}
                        color={text}
                        bold
                    >{
                        milestone.rewards.length > 0 ?
                            t('milestones.achieved', [getRewardNames(milestone)])
                        :
                            t('milestones.milestone_achieved')
                    }</Caption>
                :
                    <Caption
                        color={text}
                        fontSize={12}
                        bold
                    >{
                        type === 'spend' ? (
                            t('milestones.spends_remaining', [milestone.remaining, getRewardNames(milestone)])
                        ):(
                            t('milestones.points_remaining', [milestone.remaining, getRewardNames(milestone)])
                        )
                    }</Caption>
                }
                { openMilestone &&
                    <Box w={'100%'} >
                        <H3
                            color={text}
                            marginY={2}
                        >
                            {
                                milestone.achieved ? (
                                    t('milestones.congratulations', [milestone.name])
                                ) : (
                                    type === 'spend' ? (
                                        t('milestones.spends_remaining', [milestone.remaining, getRewardNames(milestone)])
                                    ) : (
                                        t('milestones.points_remaining', [milestone.remaining, getRewardNames(milestone)])
                                    )
                                )
                            }
                        </H3>

                        <Box>
                            <HStack space={2} mt={2}>
                                <SpIcon
                                    color={icon}
                                    icon={faGift}
                                />
                                <Caption
                                    color={text}
                                    fontWeight={'semibold'}
                                    text='milestones.benefits_title'
                                />
                            </HStack>
                            { milestone.rewards?.length && (
                                <Box mb={2} mt={2}>
                                    { milestone.rewards?.map((reward, index) =>

                                        <Box
                                            key={index}
                                            flexDirection={'row'}
                                            justifyContent={'space-between'}
                                            alignItems={'center'}
                                        >
                                            <Caption color={text} pl={6} bold>
                                                { reward?.item_name.replace(/\| /g, '\n') }
                                            </Caption>
                                            { reward.preload ?
                                                <Image
                                                    w={12}
                                                    h={12}
                                                    borderRadius={8}
                                                    uri={`${reward.preload}`}
                                                />
                                            :
                                                <SpIcon color={icon} icon={faGift} size={40} />
                                            }
                                        </Box>
                                    )}
                                </Box>
                            )}
                        </Box>
                    </Box>
                }
            </Pressable>
        </Box>
    );
};

export const CardMilestoneSkeleton = () => {

    const gStyle = useGeneralStyles();

    return (
        <VStack space={4} backgroundColor={'gray'} p={5} pr={'7rem'} bg={gStyle.section_background}>
            <HStack>
                <Skeleton h={2} rounded={'md'} flex={1} />
                <Spacer flex={1} />
                <Skeleton h={2} rounded={'md'} flex={1} />
            </HStack>
            <Skeleton h={2} rounded={'md'} w='100%' />
            <Skeleton width={'40%'} h={2} rounded={'md'} />

        </VStack>
    );
};

export default CardMilestone;
